import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { Compare } from "@emberly/zenith-client";
import { FixNumber } from "../../../../common/orders";
import { getCurrencyShorthand } from "../../../../providers/StationProvider";
import { FullTimeView } from "../../../common/core/Time";
import EntityJoinTable from "../../../common/tables/EntityJoinTable";
import { JOIN_ORDER_AND_MISSION } from "../../../../common/queries";

const Query = {
  name: "payment_requested",
  and: [
    { path: "payment.request.requested", comparer: Compare.EQ, value: true },
    { path: "payment.request.confirmed", comparer: Compare.EQ, value: false }
  ]
};

export default function RequestedPaymentsSection() {
  const { t } = useTranslation();
  const { tabId } = useParams();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("common:tables:mission"),
        flex: 0
      },
      {
        field: "payment.request.requestedDate",
        type: "date",
        valueGetter: (_, row) => !!row.payment.request?.requestedDate ? new Date(row.payment.request?.requestedDate) : null,
        renderCell: (params) => <FullTimeView time={params.row.payment.request?.requestedDate} />,
        headerName: t("requestedPayments:fieldDate"),
        minWidth: 120,
        flex: 0
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "mission.target.carDetails.registration||mission.target.boatDetails.registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row.mission.target.type;
          const target = type === 1 ? row.mission.target?.carDetails : (type === 2 ? row.mission.target.boatDetails : null);
          return target?.registration || "-";
        },
        minWidth: 100,
        flex: 0.5
      },
      {
        field: "mission.actors.requester.name||mission.actors.owner.name",
        headerName: t("common:tables:requester"),
        valueGetter: (_, row) => row.mission.actors.ownerIsNotRequester ? row.mission.actors.requester.name : row.mission.actors.owner.name,
        flex: 1,
        minWidth: 120,
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => Number(row.payment.calculatedTotal?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.payment.calculatedTotal?.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "deductible.price.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:deductible"),
        valueGetter: (_, row) => Number(row.deductible?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => params.row.deductible ? `${FixNumber(params.row.deductible?.price?.value)} ${getCurrencyShorthand(t, params.row.deductible?.price?.currency)}` : "-",
        minWidth: 120
      },
      {
        field: "payment.sender.name",
        headerName: t("entity:order:sender"),
        valueGetter: (_, row) => row.payment.sender?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "mission.actors.contract.agreement.name",
        headerName: t("entity:agreement:type"),
        valueGetter: (_, row) => row.mission?.actors?.contract?.agreement?.name || "-",
        minWidth: 160,
        flex: 0.5
      },
      {
        field: "comment",
        headerName: t("entity:order:internalComment"),
        valueGetter: (_, row) => row.comment || "-",
        minWidth: 160,
        flex: 0.5
      },
    ]
  }, [t]);

  const onRowClick = useCallback((params, ev) => {
    navigate(!tabId ? `./requested/${params.row.missionId}/${params.id}` : `./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate, tabId]);

  return (
    <EntityJoinTable
      tableId="requested_payments"
      type="Order"
      filter={Query}
      join={JOIN_ORDER_AND_MISSION}
      onRowClick={onRowClick}
      columns={columns}
      emptyStateTitle={t("requestedPayments:emptyStateTitle")}
      emptyStateDescription={t("requestedPayments:emptyStateDescription")}
      serverSidePaging
      xs={12}
    />
  );
}

